// =============================================================================
// MobileSheets.tsx
// Top-level mount point for the mobile bottom sheets — order detail, filter,
// search overlay, avatar/profile, settings. The two heaviest sheets
// (OrderDetailSheet, FilterSheet) are extracted module-level memoized
// components under sheets/ to avoid the new-function-per-render remount bug.
// Search/Avatar/Settings are simple enough to live inline here.
// =============================================================================

import React from 'react';

import { Order, ConfigData, Department } from '../../lib/types';

import { S, roleLabel, closeSheetAnim } from './mobileStyles';
import { OrderDetailSheet } from './sheets/OrderDetailSheet';
import { FilterSheet }      from './sheets/FilterSheet';
import { SheetRow }         from './sheets/SheetRow';
import type { DateRange }   from './sheets/DateRangePicker';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileSheetsProps {
  session: any;
  configData: ConfigData | null;
  currency: string;
  locale: string;
  departments: Department[];
  categories: string[];
  isDark: boolean;
  setIsDark: (v: boolean) => void;
  onSignOut: () => void;
  onOrderAdded: () => void;
  onInventoryChanged: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  selectedOrder: Order | null;
  setSelectedOrder: (o: Order | null) => void;
  setEditingOrder: (o: Order) => void;
  setShowAddOrder: (v: boolean) => void;
  showFilters: boolean;
  setShowFilters: (v: boolean) => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  filterDept: string;
  setFilterDept: (v: string) => void;
  filterCat: string;
  setFilterCat: (v: string) => void;
  showSearch: boolean;
  setShowSearch: (v: boolean) => void;
  search: string;
  setSearch: (v: string) => void;
  invSearch: string;
  setInvSearch: (v: string) => void;
  activeTab: string;
  showAvatar: boolean;
  setShowAvatar: (v: boolean) => void;
  showSettings: boolean;
  setShowSettings: (v: boolean) => void;
  closingSheet: string | null;
  setClosingSheet: (v: string | null) => void;
  dateRange: DateRange;
  onOpenDateRange: () => void;
  onClearDateRange: () => void;
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileSheets(props: MobileSheetsProps) {
  const {
    session, configData, currency, locale, departments, categories,
    isDark, setIsDark, onSignOut, onOrderAdded, onInventoryChanged, showToast,
    selectedOrder, setSelectedOrder, setEditingOrder, setShowAddOrder,
    showFilters, setShowFilters, filterStatus, setFilterStatus, filterDept, setFilterDept, filterCat, setFilterCat,
    showSearch, setShowSearch, search, setSearch, invSearch, setInvSearch, activeTab,
    showAvatar, setShowAvatar,
    showSettings, setShowSettings,
    closingSheet, setClosingSheet,
    dateRange, onOpenDateRange, onClearDateRange,
  } = props;

  return (
    <>
      <OrderDetailSheet
        selectedOrder={selectedOrder}
        setSelectedOrder={setSelectedOrder}
        setEditingOrder={setEditingOrder}
        setShowAddOrder={setShowAddOrder}
        closingSheet={closingSheet}
        setClosingSheet={setClosingSheet}
        currency={currency}
        locale={locale}
        onOrderAdded={onOrderAdded}
        onInventoryChanged={onInventoryChanged}
        showToast={showToast}
      />

      <FilterSheet
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterDept={filterDept}
        setFilterDept={setFilterDept}
        filterCat={filterCat}
        setFilterCat={setFilterCat}
        departments={departments}
        categories={categories}
        closingSheet={closingSheet}
        setClosingSheet={setClosingSheet}
        dateRange={dateRange}
        onOpenDateRange={onOpenDateRange}
        onClearDateRange={onClearDateRange}
      />

      {/* Search overlay */}
      {showSearch && (
        <div style={S.sheet} className={closingSheet === 'search' ? 'm-sheet-closing' : 'm-sheet-backdrop'} onClick={() => closeSheetAnim(setClosingSheet, setShowSearch, 'search')}>
          <div style={{ ...S.sheetContent, maxHeight: '50vh' }} className="m-sheet-content" onClick={e => e.stopPropagation()}>
            <div style={S.handle as any} />
            <div style={{ padding: '8px 20px 16px' }}>
              <input autoFocus value={activeTab === 'inventory' ? invSearch : search}
                onChange={e => activeTab === 'inventory' ? setInvSearch(e.target.value) : setSearch(e.target.value)}
                placeholder={activeTab === 'inventory' ? 'Search items...' : 'Search orders...'}
                style={{ ...S.input, padding: '12px 14px', borderRadius: 12 }} />
            </div>
          </div>
        </div>
      )}

      {/* Avatar menu — both role pills equal-weight tonal grey, body rows
          unified through <SheetRow>, footer left-aligned to match. */}
      {showAvatar && (
        <div style={S.sheet} className={closingSheet === 'avatar' ? 'm-sheet-closing' : 'm-sheet-backdrop'} onClick={() => closeSheetAnim(setClosingSheet, setShowAvatar, 'avatar')}>
          <div style={{ ...S.sheetContent, maxHeight: '55vh' }} className="m-sheet-content" onClick={e => e.stopPropagation()}>
            <div style={S.handle as any} />
            <div style={{ padding: '16px 20px 12px', borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{configData?.profile?.display_name || session?.user?.email}</div>
              <div style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text3)', marginTop: 2 }}>{session?.user?.email}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {configData?.profile?.role && <span className="pill-u gray">{roleLabel(configData.profile.role)}</span>}
                {configData?.profile?.department && <span className="pill-u gray">{configData.profile.department}</span>}
              </div>
            </div>
            <div style={{ padding: '8px 12px' }}>
              <SheetRow
                icon={<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>}
                label="Dark mode"
                right={
                  <button onClick={() => setIsDark(!isDark)} aria-label="Toggle dark mode" style={{ width: 42, height: 24, background: isDark ? 'var(--accent)' : 'var(--border2)', borderRadius: 12, border: 'none', position: 'relative', cursor: 'pointer' }}>
                    <div style={{ width: 20, height: 20, background: 'var(--surface)', borderRadius: '50%', position: 'absolute', top: 2, left: isDark ? 20 : 2, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                  </button>
                }
              />
              <SheetRow
                icon={<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3" /><path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z" /></svg>}
                label="Settings"
                onClick={() => { setShowAvatar(false); setShowSettings(true); }}
              />
              <div style={{ height: '0.5px', background: 'var(--border)', margin: '4px 10px' }} />
              <SheetRow
                icon={<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 3H5a2 2 0 00-2 2v14a2 2 0 002 2h4M16 17l5-5-5-5M11 12h10" /></svg>}
                label="Sign out"
                destructive
                onClick={() => { closeSheetAnim(setClosingSheet, setShowAvatar, 'avatar'); setTimeout(onSignOut, 400); }}
              />
            </div>
            <div style={{ padding: '8px 22px 4px', textAlign: 'left' as const }}>
              <span style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>GoPal v1.0 · getgopal.co.nz</span>
            </div>
          </div>
        </div>
      )}

      {/* Settings sheet */}
      {showSettings && (
        <div style={S.sheet} className={closingSheet === 'settings' ? 'm-sheet-closing' : 'm-sheet-backdrop'} onClick={() => closeSheetAnim(setClosingSheet, setShowSettings, 'settings')}>
          <div style={{ ...S.sheetContent, maxHeight: '80vh' }} className="m-sheet-content" onClick={e => e.stopPropagation()}>
            <div style={S.handle as any} />
            <div style={{ padding: '8px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '0.5px solid var(--border)' }}>
              <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)' }}>Settings</span>
              <button onClick={() => closeSheetAnim(setClosingSheet, setShowSettings, 'settings')} style={{ fontSize: 14, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}>Done</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Organisation</div>
                <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 500, color: 'var(--text)' }}>{configData?.config?.org_name || 'GoPal'}</div>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Departments</div>
                {departments.map(d => (
                  <div key={d.id} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 14px', marginBottom: 4, fontSize: 'var(--m-text-meta)', color: 'var(--text)' }}>{d.name}</div>
                ))}
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Categories</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {categories.map(c => (
                    <span key={c} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 'var(--m-text-label)', background: 'var(--surface2)', color: 'var(--text)' }}>{c}</span>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 'var(--m-text-label)', fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Your role</div>
                <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 14px', fontSize: 'var(--m-text-meta)', color: 'var(--text)' }}>{configData?.profile?.role ? roleLabel(configData.profile.role) : 'User'}</div>
              </div>
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', textAlign: 'center', marginTop: 24 }}>
                Full settings available on desktop
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
